import React, { useCallback, useState } from "react";
import { Animated, StyleSheet, Text, TextInput, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTranslation } from "react-i18next";

import Colors from "../../../../Resources/Color";
import FloatingMenu from "../../../Components/FloatingMenu";
import HoverableTouchableOpacity from "../../../Components/HoverableTouchableOpacity";
import Tooltip from "../../../Components/Tooltip";
import { useDevice } from "../../../_layout";
import useChevronRotation from "../../../hooks/useChevronRotation";
import { MAX_NUTRITION_AMOUNT_INPUT_LENGTH } from "../../nutritionLimits";
import { formatQuantityDisplayValue } from "../../recipes/recipeLogic";
import {
  formatCalorieValue,
  formatMacroValue,
  getUiMacroShortLabel,
  UI_MACRO_DISPLAY_ORDER,
} from "../nutritionFormatters";

const INGREDIENT_ROW_INLINE_MIN_WIDTH = 560;

export { formatCalorieValue, formatMacroValue };

export function useResponsiveIngredientRowLayout(baseCompactLayout) {
  const [measuredRowWidth, setMeasuredRowWidth] = useState(0);

  const handleRowLayout = useCallback((event) => {
    const nextWidth = Math.round(event?.nativeEvent?.layout?.width ?? 0);

    setMeasuredRowWidth((currentWidth) =>
      currentWidth === nextWidth ? currentWidth : nextWidth,
    );
  }, []);

  return {
    handleRowLayout,
    shouldUseCompactLayout:
      baseCompactLayout ||
      (measuredRowWidth > 0 &&
        measuredRowWidth < INGREDIENT_ROW_INLINE_MIN_WIDTH),
  };
}

export function QuantityControl({
  value,
  onChangeText,
  onSubmitEditing,
  unitOptions = [],
  selectedUnitKey = "grams",
  onSelectUnit,
  referenceText = "100g",
  isAutoCalculated = false,
  autoCalculatedTooltip = "",
  quantityLinkTooltip = "",
  onToggleQuantityLink = null,
  isReadOnly = false,
  chromeVariant = "default",
}) {
  const [isEditableControlHovered, setIsEditableControlHovered] = useState(false);
  const [isUnitSelectorHovered, setIsUnitSelectorHovered] = useState(false);
  const [isUnitMenuOpen, setIsUnitMenuOpen] = useState(false);
  const { isMobile } = useDevice();
  const selectedUnitLabel =
    unitOptions.find((option) => option.id === selectedUnitKey)?.label ?? "g";
  const showUnitSelector = unitOptions.length > 1;
  const resolvedQuantityValue = isAutoCalculated
    ? formatQuantityDisplayValue(value, { zeroFallback: true })
    : value;
  const unitChevronRotation = useChevronRotation(isUnitMenuOpen, "closedDownOpenUp");
  const isQuantityEditable = !isAutoCalculated && !isReadOnly;
  const canToggleQuantityLink =
    typeof onToggleQuantityLink === "function" && !isReadOnly;
  const shouldShowLinkIcon = isAutoCalculated || canToggleQuantityLink;
  const linkTooltip = quantityLinkTooltip || autoCalculatedTooltip;

  return (
    <View style={styles.quantityControlWrap}>
      {shouldShowLinkIcon ? (
        <View style={styles.autoCalculatedIconOverlay}>
          <Tooltip
            content={linkTooltip}
            placement="top"
            rawText={Boolean(linkTooltip)}
            disabled={!linkTooltip}
          >
            <View>
              {canToggleQuantityLink ? (
                <HoverableTouchableOpacity
                  activeOpacity={0.85}
                  onPress={onToggleQuantityLink}
                  style={styles.quantityLinkIconButton}
                  hoverStyle={styles.quantityLinkIconButtonHover}
                  title={linkTooltip}
                  accessibilityLabel={linkTooltip}
                >
                  <Ionicons
                    name={isAutoCalculated ? "link" : "unlink-outline"}
                    size={12}
                    color={isAutoCalculated ? Colors.bubbleColor : Colors.grayColor}
                  />
                </HoverableTouchableOpacity>
              ) : (
                <Ionicons
                  name="link"
                  size={12}
                  color={Colors.bubbleColor}
                />
              )}
            </View>
          </Tooltip>
        </View>
      ) : null}
      <View
        style={[
          styles.quantityControl,
          chromeVariant === "transparent"
            ? styles.quantityControlTransparent
            : null,
          isAutoCalculated ? styles.quantityControlAutoCalculated : null,
          isQuantityEditable && isEditableControlHovered
            ? styles.quantityControlEditableHover
            : null,
        ]}
        onMouseEnter={
          isQuantityEditable && !isMobile
            ? () => setIsEditableControlHovered(true)
            : undefined
        }
        onMouseLeave={
          isQuantityEditable ? () => setIsEditableControlHovered(false) : undefined
        }
      >
        <View style={[styles.quantitySideSegment, styles.quantityInputSegment]}>
          {isAutoCalculated ? (
            <Tooltip content={autoCalculatedTooltip} placement="top" rawText={true}>
              <View style={styles.quantityInputWrap}>
                <TextInput
                  value={resolvedQuantityValue}
                  onChangeText={undefined}
                  onSubmitEditing={undefined}
                  keyboardType="decimal-pad"
                  style={[
                    styles.quantityInput,
                    styles.quantityInputAutoCalculated,
                  ]}
                  placeholder="100"
                  placeholderTextColor={Colors.grayColor}
                  editable={false}
                  caretHidden={true}
                  showSoftInputOnFocus={false}
                />
              </View>
            </Tooltip>
          ) : (
            <View style={styles.quantityInputWrap}>
              <TextInput
                value={resolvedQuantityValue}
                onChangeText={isReadOnly ? undefined : onChangeText}
                onSubmitEditing={isReadOnly ? undefined : onSubmitEditing}
                keyboardType="decimal-pad"
                maxLength={MAX_NUTRITION_AMOUNT_INPUT_LENGTH}
                style={styles.quantityInput}
                placeholder="100"
                placeholderTextColor={Colors.grayColor}
                editable={!isReadOnly}
                caretHidden={isReadOnly}
                showSoftInputOnFocus={!isReadOnly}
              />
            </View>
          )}
        </View>
        {showUnitSelector && !isReadOnly ? (
          <FloatingMenu
            placement="bottom-start"
            width={180}
            items={unitOptions}
            open={isUnitMenuOpen}
            onOpenChange={setIsUnitMenuOpen}
            triggerContainerStyle={[
              styles.quantityCenterSegment,
              styles.quantityUnitSegment,
            ]}
            trigger={
              <View
                style={[
                  styles.quantityUnitPill,
                  styles.quantityCenterSegment,
                  styles.quantityUnitSegment,
                ]}
                onMouseEnter={() => !isMobile && setIsUnitSelectorHovered(true)}
                onMouseLeave={() => setIsUnitSelectorHovered(false)}
              >
                <View style={styles.quantityUnitContent}>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.quantityUnitText,
                    styles.quantityUnitTextInteractive,
                  ]}
                >
                  {selectedUnitLabel}
                  </Text>
                  <Animated.View
                    style={{ transform: [{ rotate: unitChevronRotation }] }}
                  >
                    <Ionicons
                      name="chevron-down"
                      size={14}
                      color={
                        isUnitSelectorHovered
                          ? Colors.blackColor
                          : Colors.grayColor
                      }
                    />
                  </Animated.View>
                </View>
              </View>
            }
            renderItem={({ item, close }) => (
              <HoverableTouchableOpacity
                style={styles.menuItem}
                hoverStyle={styles.menuItemHover}
                onPress={() => {
                  onSelectUnit?.(item.id);
                  close();
                }}
              >
                <Text
                  style={[
                    styles.menuItemText,
                    item.id === selectedUnitKey
                      ? styles.menuItemTextActive
                      : null,
                  ]}
                >
                  {item.label}
                </Text>
                {item.id === selectedUnitKey ? (
                  <Ionicons
                    name="checkmark"
                    size={15}
                    color={Colors.bubbleColor}
                  />
                ) : null}
              </HoverableTouchableOpacity>
            )}
          />
        ) : (
          <View
            style={[
              styles.quantityCenterSegment,
              styles.quantityUnitSegment,
              styles.quantityUnitPill,
              styles.quantityUnitPillStatic,
            ]}
          >
            <Text
              numberOfLines={1}
              style={[
              styles.quantityUnitText,
              isAutoCalculated || isReadOnly
                ? styles.quantityUnitTextMuted
                : styles.quantityUnitTextInteractive,
            ]}
            >
              {selectedUnitLabel}
            </Text>
          </View>
        )}
        <View
          style={[
            styles.quantitySideSegment,
            styles.quantityReferenceSegment,
            styles.quantityReferencePill,
          ]}
        >
          <Text numberOfLines={1} style={styles.quantityReferenceText}>
            {referenceText}
          </Text>
        </View>
      </View>
    </View>
  );
}

export function InlineNutritionSummary({
  calories,
  fats,
  carbs,
  protein,
  showCalories = true,
}) {
  const { i18n } = useTranslation();
  const language = i18n?.resolvedLanguage || i18n?.language || "en";
  const macroValues = {
    protein,
    carbs,
    fats,
  };
  const macroItems = UI_MACRO_DISPLAY_ORDER.map((key) => ({
    key,
    shortLabel: getUiMacroShortLabel(key, language),
    value: `${formatMacroValue(macroValues[key])}g`,
  }));

  return (
    <View style={styles.selectedIngredientMacroRow}>
      {showCalories ? (
        <>
          <Text style={styles.selectedIngredientMacroValue}>
            E{" "}
            <Text style={styles.selectedIngredientEnergyNum}>
              {formatCalorieValue(calories)} kcal
            </Text>
          </Text>
          <View style={styles.selectedIngredientMacroDivider} />
        </>
      ) : null}
      {macroItems.map((item) => (
        <Text key={item.key} style={styles.selectedIngredientMacroValue}>
          {item.shortLabel}{" "}
          <Text style={styles.selectedIngredientMacroNum}>{item.value}</Text>
        </Text>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  menuItem: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderRadius: 8,
  },
  menuItemHover: {
    backgroundColor: Colors.hoveredGray,
  },
  menuItemText: {
    fontSize: 12,
    fontWeight: "400",
    color: Colors.darkgrayColor,
    flexShrink: 1,
  },
  menuItemTextActive: {
    fontWeight: "500",
    color: Colors.bubbleColor,
  },
  quantityControl: {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    height: 36,
    borderRadius: 10,
    overflow: "hidden",
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    flexDirection: "row",
    alignItems: "stretch",
  },
  quantityControlTransparent: {
    backgroundColor: "transparent",
  },
  quantityControlWrap: {
    flexBasis: 188,
    flexShrink: 1,
    flexGrow: 0,
    minWidth: 136,
    maxWidth: "50%",
    position: "relative",
  },
  quantityControlAutoCalculated: {
  },
  quantityControlEditableHover: {
    borderColor: "rgb(196, 196, 196)",
  },
  quantitySideSegment: {
    flexBasis: 54,
    flexGrow: 0,
    flexShrink: 1,
    minWidth: 0,
    height: "100%",
    borderTopLeftRadius: 10,
    borderBottomLeftRadius: 10,
  },
  quantityCenterSegment: {
    flexBasis: 79,
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 34,
    maxWidth: "100%",
    height: "100%",
  },
  quantityInputSegment: {
    flexBasis: 55,
    minWidth: 36,
  },
  quantityReferenceSegment: {
    flexBasis: 54,
    minWidth: 36,
  },
  quantityUnitSegment: {
    minWidth: 0,
  },
  quantityInput: {
    width: "100%",
    height: "100%",
    paddingHorizontal: 6,
    paddingVertical: 0,
    fontSize: 12,
    fontWeight: "400",
    color: Colors.blackColor,
    textAlign: "center",
    outlineStyle: "none",
  },
  quantityInputWrap: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    borderRightWidth: 0.5,
    borderRightColor: Colors.borderColor,
    borderTopLeftRadius: 10,
    borderBottomLeftRadius: 10,
    overflow: "hidden",
  },
  quantityInputAutoCalculated: {
    color: Colors.grayColor,
  },
  autoCalculatedIconOverlay: {
    position: "absolute",
    left: -10,
    top: "50%",
    marginTop: -10,
    zIndex: 2,
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
  },
  quantityLinkIconButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  quantityLinkIconButtonHover: {
    backgroundColor: "rgba(63, 123, 141, 0.08)",
  },
  quantityUnitPill: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    paddingHorizontal: 6,
    borderRightWidth: 0.5,
    borderRightColor: Colors.borderColor,
  },
  quantityUnitPillStatic: {
    justifyContent: "center",
  },
  quantityUnitContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    maxWidth: "100%",
  },
  quantityUnitText: {
    fontSize: 12,
    fontWeight: "400",
    flexShrink: 1,
    textAlign: "center",
  },
  quantityUnitTextInteractive: {
    color: Colors.blackColor,
  },
  quantityUnitTextMuted: {
    color: Colors.grayColor,
  },
  quantityReferencePill: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    paddingHorizontal: 6,
  },
  quantityReferenceText: {
    fontSize: 12,
    fontWeight: "400",
    color: Colors.grayColor,
    textAlign: "center",
  },
  selectedIngredientMacroRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
    flexShrink: 1,
    rowGap: 4,
  },
  selectedIngredientMacroDivider: {
    width: 1,
    height: 12,
    backgroundColor: "rgba(63, 123, 141, 0.18)",
  },
  selectedIngredientMacroValue: {
    fontSize: 11,
    fontWeight: "400",
    letterSpacing: 0.2,
    color: Colors.grayColor,
  },
  selectedIngredientMacroNum: {
    fontSize: 11,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  selectedIngredientEnergyNum: {
    fontSize: 11,
    fontWeight: "400",
    color: Colors.darkgrayColor,
  },
});
